/**
 * PokeCCC cross-domain language switcher.
 *
 * Routing, currency and preference handling all live in pokeccc-locale.js,
 * which has already run (synchronously, from <head>) by the time this file
 * executes. What is left here:
 *
 *   - the geo fallback, for visitors with no stored choice and no usable
 *     browser language
 *   - stripping the ?pklang= handover parameter from the address bar
 *   - the switcher UI: desktop dropdown, mobile sheet, keyboard support
 */
(function () {
  'use strict';

  var LOG_PREFIX = '[PokecccLangSwitcher]';
  var DESKTOP_QUERY = '(min-width: 750px)';
  var CLOSE_DELAY_MS = 120;

  var core = window.PokeCCC;
  if (!core || !core.ready) {
    console.warn(LOG_PREFIX, 'Shared locale runtime unavailable');
    return;
  }

  var config = core.config;

  /* ------------------------------------------------------------------ */
  /* Address bar cleanup                                                 */
  /* ------------------------------------------------------------------ */

  function cleanUrlParam() {
    var params = new URLSearchParams(window.location.search);
    if (!params.has(config.crossDomainParam)) return;

    params.delete(config.crossDomainParam);
    var query = params.toString();
    window.history.replaceState(
      null,
      '',
      window.location.pathname +
        (query ? '?' + query : '') +
        window.location.hash
    );
  }

  /* ------------------------------------------------------------------ */
  /* Geo fallback                                                        */
  /* ------------------------------------------------------------------ */

  /**
   * Only reached when the visitor gave us nothing to go on. Resolves to true
   * when a redirect was started, so the UI is not bound to a dying document.
   */
  function runGeoFallback() {
    if (!core.fastPath.redirectsAllowed) {
      core.routing.clearRedirectCount();
      return Promise.resolve(false);
    }

    return core.geo.detect().then(function (countryCode) {
      if (!countryCode) {
        // Detection failed. Staying put is the only safe option: the old
        // fallback assumed CZ, which shipped English visitors to the Czech
        // store whenever the endpoint hiccuped.
        core.routing.clearRedirectCount();
        return false;
      }

      var lang = core.geo.countryToLang(countryCode);
      console.log(LOG_PREFIX, 'Detected', countryCode, '-> language', lang);

      if (lang === config.locale) {
        core.currency.syncForLang(lang);
        core.routing.clearRedirectCount();
        return false;
      }

      core.currency.syncForLang(lang);
      var redirecting = core.routing.navigate(lang, { replace: true });
      if (!redirecting) core.routing.clearRedirectCount();
      return redirecting;
    });
  }

  /* ------------------------------------------------------------------ */
  /* Listbox behaviour (shared by the dropdown and the mobile sheet)      */
  /* ------------------------------------------------------------------ */

  /**
   * Wires roving-tabindex keyboard support onto a `role="listbox"` container
   * whose children are `role="option"` elements.
   */
  function createListbox(container, onSelect) {
    if (!container) return null;

    var options = Array.prototype.slice.call(
      container.querySelectorAll('[role="option"]')
    );
    if (!options.length) return null;

    function focusOption(index) {
      var bounded = (index + options.length) % options.length;
      options.forEach(function (option, i) {
        option.setAttribute('tabindex', i === bounded ? '0' : '-1');
      });
      options[bounded].focus();
    }

    function indexOfActive() {
      var current = options.indexOf(document.activeElement);
      if (current !== -1) return current;
      var selected = options.findIndex(function (option) {
        return option.getAttribute('aria-selected') === 'true';
      });
      return selected === -1 ? 0 : selected;
    }

    options.forEach(function (option) {
      option.addEventListener('click', function (event) {
        event.stopPropagation();
        onSelect(option.dataset.locale);
      });
    });

    container.addEventListener('keydown', function (event) {
      switch (event.key) {
        case 'ArrowDown':
        case 'ArrowRight':
          event.preventDefault();
          focusOption(indexOfActive() + 1);
          break;
        case 'ArrowUp':
        case 'ArrowLeft':
          event.preventDefault();
          focusOption(indexOfActive() - 1);
          break;
        case 'Home':
          event.preventDefault();
          focusOption(0);
          break;
        case 'End':
          event.preventDefault();
          focusOption(options.length - 1);
          break;
        case 'Enter':
        case ' ':
        case 'Spacebar':
          event.preventDefault();
          if (document.activeElement && document.activeElement.dataset.locale) {
            onSelect(document.activeElement.dataset.locale);
          }
          break;
        default:
          break;
      }
    });

    return {
      container: container,
      options: options,
      focusSelected: function () {
        focusOption(indexOfActive());
      },
      first: function () {
        return options[0];
      },
      last: function () {
        return options[options.length - 1];
      },
    };
  }

  /* ------------------------------------------------------------------ */
  /* UI                                                                  */
  /* ------------------------------------------------------------------ */

  function initUI() {
    var trigger = document.getElementById('custom-lang-btn');
    var dropdown = document.getElementById('custom-lang-dropdown');
    var overlay = document.getElementById('custom-lang-overlay');
    var sheet = document.getElementById('custom-lang-sheet');

    // Layouts without a header (PageFly pages) get the redirect but have no
    // switcher to bind. That is expected, not a fault.
    if (!trigger) return;

    var desktop = window.matchMedia(DESKTOP_QUERY);
    var closeTimer = null;
    var dropdownOpen = false;
    var sheetOpen = false;

    function switchLang(lang) {
      if (!core.isValidLang(lang) || lang === config.locale) {
        closeAll();
        return;
      }

      core.prefs.setLang(lang);
      core.currency.syncForLang(lang);
      core.currency.clearForced();

      // A cart cannot follow the visitor to the other domain, so land them on
      // its home page rather than on an alarmingly empty cart.
      var options = {};
      if (core.routing.isCartOrCheckout(window.location.pathname)) {
        options.path = '/';
      }

      core.routing.navigate(lang, options);
    }

    var dropdownListbox = createListbox(dropdown, switchLang);
    var sheetListbox = createListbox(
      sheet ? sheet.querySelector('[role="listbox"]') : null,
      switchLang
    );

    /* ---- desktop dropdown ---- */

    function openDropdown(withFocus) {
      if (!dropdown) return;
      clearTimeout(closeTimer);
      dropdown.classList.add('open');
      trigger.setAttribute('aria-expanded', 'true');
      dropdownOpen = true;
      if (withFocus && dropdownListbox) dropdownListbox.focusSelected();
    }

    function closeDropdown(restoreFocus) {
      if (!dropdown) return;
      clearTimeout(closeTimer);
      dropdown.classList.remove('open');
      trigger.setAttribute('aria-expanded', 'false');
      dropdownOpen = false;
      if (restoreFocus) trigger.focus();
    }

    function scheduleCloseDropdown() {
      if (!dropdown) return;
      clearTimeout(closeTimer);
      closeTimer = setTimeout(function () {
        // Never yank the panel away from a keyboard user inside it.
        if (dropdown.contains(document.activeElement)) return;
        closeDropdown(false);
      }, CLOSE_DELAY_MS);
    }

    /* ---- mobile sheet ---- */

    function openSheet() {
      if (!overlay) return;
      overlay.classList.add('visible');
      sheetOpen = true;
      trigger.setAttribute('aria-expanded', 'true');
      requestAnimationFrame(function () {
        overlay.classList.add('open');
        if (sheetListbox) sheetListbox.focusSelected();
      });
    }

    function closeSheet(restoreFocus) {
      if (!overlay || !sheetOpen) return;
      sheetOpen = false;
      trigger.setAttribute('aria-expanded', 'false');
      overlay.classList.remove('open');
      overlay.addEventListener(
        'transitionend',
        function () {
          if (!sheetOpen) overlay.classList.remove('visible');
        },
        { once: true }
      );
      if (restoreFocus) trigger.focus();
    }

    function closeAll() {
      closeDropdown(false);
      closeSheet(false);
    }

    /* ---- trigger ---- */

    trigger.addEventListener('click', function (event) {
      event.stopPropagation();
      if (desktop.matches) {
        if (dropdownOpen) closeDropdown(false);
        else openDropdown(false);
      } else if (sheetOpen) {
        closeSheet(true);
      } else {
        openSheet();
      }
    });

    trigger.addEventListener('keydown', function (event) {
      if (
        event.key === 'ArrowDown' ||
        event.key === 'Enter' ||
        event.key === ' ' ||
        event.key === 'Spacebar'
      ) {
        event.preventDefault();
        if (desktop.matches) openDropdown(true);
        else openSheet();
      }
    });

    trigger.addEventListener('mouseenter', function () {
      if (desktop.matches) openDropdown(false);
    });

    trigger.addEventListener('mouseleave', function (event) {
      if (desktop.matches && dropdown && !dropdown.contains(event.relatedTarget)) {
        scheduleCloseDropdown();
      }
    });

    if (dropdown) {
      dropdown.addEventListener('mouseenter', function () {
        if (desktop.matches) clearTimeout(closeTimer);
      });
      dropdown.addEventListener('mouseleave', function (event) {
        if (desktop.matches && !trigger.contains(event.relatedTarget)) {
          scheduleCloseDropdown();
        }
      });
      dropdown.addEventListener('focusout', function (event) {
        if (
          !dropdown.contains(event.relatedTarget) &&
          event.relatedTarget !== trigger
        ) {
          closeDropdown(false);
        }
      });
    }

    if (overlay) {
      overlay.addEventListener('click', function (event) {
        if (event.target === overlay) closeSheet(true);
      });
    }

    // The sheet is a modal dialog: keep Tab inside it.
    if (sheet && sheetListbox) {
      sheet.addEventListener('keydown', function (event) {
        if (event.key !== 'Tab') return;
        var first = sheetListbox.first();
        var last = sheetListbox.last();
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      });
    }

    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape') return;
      if (sheetOpen) {
        event.stopPropagation();
        closeSheet(true);
      } else if (dropdownOpen) {
        event.stopPropagation();
        closeDropdown(true);
      }
    });

    document.addEventListener('click', function () {
      if (dropdownOpen) closeDropdown(false);
    });

    // Viewport crossed the breakpoint: drop whichever surface no longer applies.
    var onBreakpointChange = function () {
      closeAll();
    };
    if (desktop.addEventListener) desktop.addEventListener('change', onBreakpointChange);
    else desktop.addListener(onBreakpointChange);
  }

  /* ------------------------------------------------------------------ */

  function init() {
    if (core.abort) return;

    cleanUrlParam();
    core.currency.check();

    if (core.fastPath.redirecting) {
      // Document is on its way out.
      return;
    }

    if (core.fastPath.lang) {
      initUI();
      return;
    }

    runGeoFallback().then(function (redirecting) {
      if (!redirecting) initUI();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
